/**
 * Shared error-tracking filter for transient ClerkJS session-heartbeat network
 * failures.
 *
 * `@clerk/clerk-js` fires a periodic session heartbeat against
 * `/v1/client/sessions/<id>/touch`. On a flaky connection — common for our
 * mobile-first audience — the underlying `fetch` rejects and Clerk re-throws it
 * from inside its (minified) bundle as, e.g.:
 *
 *   ClerkJS: Network error at "https://clerk.go.shipmate.jp/v1/client/sessions/
 *   sess_XXX/touch?__clerk_api_version=2026-05-12&_clerk_js_version=6.22.0"
 *   - TypeError: Load failed (clerk.go.shipmate.jp). Please try again.
 *
 * These are not application defects (every stack frame sits in the third-party
 * Clerk bundle) and nothing in any user flow is broken — we cannot stop a
 * third-party heartbeat from failing on a flaky connection. Because the message
 * embeds the session id and `_clerk_js_version`, each occurrence gets a unique
 * fingerprint and fragments into a brand-new error-tracking issue, so the same
 * un-actionable hiccup becomes recurring triage noise.
 *
 * Both Clerk-based Next.js apps (`apps/app-dashboard`, `apps/dashboard`) capture
 * exceptions to PostHog (`capture_exceptions`) and Sentry, so this predicate is
 * wired into the `before_send` / `beforeSend` hooks of both to drop these
 * events before they create issues.
 */

/** Minimal shape of an exception entry from either PostHog or Sentry. */
export interface ExceptionLike {
  type?: unknown;
  value?: unknown;
}

// The session-touch heartbeat URL embedded in the Clerk-wrapped message. Host
// agnostic so it matches both clerk.shipmate.jp and clerk.go.shipmate.jp.
const CLERK_SESSION_TOUCH_RE = /\/v1\/client\/sessions\/[^/]+\/touch/;

// The underlying failure is always a network fetch rejection, surfaced by the
// browser as "Failed to fetch" (Chromium) or "Load failed" (WebKit/Safari).
const NETWORK_ERROR_RE =
  /failed to fetch|load failed|networkerror|network error/i;

/**
 * Returns true when the given exception list represents a transient Clerk
 * session-`touch` heartbeat network failure that should be dropped from error
 * tracking. Requires BOTH the touch URL and a network-error phrase so that
 * genuine Clerk errors (e.g. an auth failure on the same endpoint) still report.
 */
export function isClerkSessionTouchNetworkError(
  exceptions: readonly ExceptionLike[] | undefined | null,
): boolean {
  if (!exceptions?.length) return false;
  return exceptions.some((ex) => {
    const type = typeof ex.type === "string" ? ex.type : "";
    const value = typeof ex.value === "string" ? ex.value : "";
    const text = `${type} ${value}`;
    return CLERK_SESSION_TOUCH_RE.test(text) && NETWORK_ERROR_RE.test(text);
  });
}
